import { supabase } from './supabaseClient';
import type { ProductReview } from '@/types/product';

export interface MyReviewStatus {
  id: string;
  rating: number;
  reviewText: string;
  status: 'pending' | 'approved' | 'rejected';
}

interface ReviewRow {
  id: string;
  customer_name: string;
  skin_type: string | null;
  rating: number;
  review_text: string;
  verified_purchase: boolean;
  created_at: string;
}

export async function fetchApprovedReviews(sku: string): Promise<ProductReview[]> {
  const { data } = await supabase
    .from('rxk_product_reviews')
    .select('id, customer_name, skin_type, rating, review_text, verified_purchase, created_at')
    .eq('sku', sku)
    .eq('status', 'approved')
    .order('created_at', { ascending: false });

  return ((data as ReviewRow[]) ?? []).map((r) => ({
    name: r.customer_name,
    skin: r.skin_type ?? '',
    rating: r.rating,
    date: new Date(r.created_at).toLocaleDateString('en-CA'),
    text: r.review_text,
    verified: r.verified_purchase,
  }));
}

export async function fetchMyReview(sku: string, customerId: string): Promise<MyReviewStatus | null> {
  const { data } = await supabase
    .from('rxk_product_reviews')
    .select('id, rating, review_text, status')
    .eq('sku', sku)
    .eq('customer_id', customerId)
    .maybeSingle();
  if (!data) return null;
  return {
    id: data.id,
    rating: data.rating,
    reviewText: data.review_text,
    status: data.status,
  };
}

async function hasVerifiedPurchase(sku: string): Promise<boolean> {
  const { data } = await supabase
    .from('core_order_items')
    .select('id, core_orders!inner(status)')
    .eq('sku', sku)
    .not('core_orders.status', 'in', '(cancelled,returned)')
    .limit(1);
  return (data?.length ?? 0) > 0;
}

export async function submitReview(input: {
  sku: string;
  customerId: string;
  customerName: string;
  skinType: string;
  rating: number;
  reviewText: string;
}): Promise<void> {
  const verified = await hasVerifiedPurchase(input.sku);
  const { error } = await supabase.from('rxk_product_reviews').insert({
    sku: input.sku,
    customer_id: input.customerId,
    customer_name: input.customerName,
    skin_type: input.skinType || null,
    rating: input.rating,
    review_text: input.reviewText,
    verified_purchase: verified,
  });
  if (error) throw error;
}
